import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket";
import { useDebounce } from "../../../../hooks/usedebounce";
import {
  ChartListResponse,
  ChartOrderBy,
  ChartStatus,
  ChartStatusEnum,
  ChartTypeEnum,
} from "../../../../models/chart";
import { ChartsService } from "../../../../services/charts-service";
import { CognitoService } from "../../../../services/cognito-service";

export type AdminChartsState = {
  loading: boolean;
  query: string;
  debouncedQuery: string;
  status: ChartStatus;
  orderBy: ChartOrderBy;
  charts: ChartListResponse[][];
  wasRequestSent: boolean;
  showCompletedTestDecks: boolean;
  counties: string[];
  chartType: ChartTypeEnum;

  downloadChart: (chartId: string, type: "download" | "print") => Promise<void>;
  downloadTestDeck: (chartId: string) => Promise<void>;
  downloadAll: (chartIds: string[], type: "charts" | "decks") => Promise<void>;
  sendRequest: () => void;
  editTestDeck: (chartId: string) => void;
  startNewChart: () => void;
  duplicateChart: (chartId: string) => void;
  deleteChart: (id: string) => void;
  updateStatus: (status: ChartStatus) => void;
  updateOrderBy: (orderBy: ChartOrderBy) => void;
  updateShowCompletedTestDecks: (show: boolean) => void;
  setQuery: (query: string) => void;
  getDate: (date: string) => string;
  regenerateChart: (chartId: string) => void;
  regenerateTestDeck: (chartId: string) => void;
};

export default function useAdminCharts(
  chartType: ChartTypeEnum
): AdminChartsState {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [query, setQuery] = useState<string>("");
  const [wasRequestSent, setWasRequestSent] = useState<boolean>(false);
  const [status, setStatus] = useState<ChartStatus>(
    Object.keys(ChartStatusEnum).includes(searchParams.get("status") || "")
      ? (searchParams.get("status") as ChartStatus)
      : "InProgress"
  );
  const [orderBy, setOrderBy] = useState<ChartOrderBy>("modifiedAt");
  const [showCompletedTestDecks, setShowCompletedTestDecks] = useState<boolean>(
    status === "Completed" && searchParams.get("showCompletedDeck") === "true"
  );
  const [charts, setCharts] = useState<ChartListResponse[][]>([]);
  const [counties, setCounties] = useState<string[]>([]);
  const debouncedQuery = useDebounce(query, 800);
  const [websocketUrl, setWebsocketUrl] = useState("");
  const [token, setToken] = useState<string>("");
  const [shouldWebSocketConnect, setShouldWebSocketConnect] = useState(false);

  useWebSocket(
    websocketUrl,
    {
      share: false,
      shouldReconnect: (closeEvent) => false,
      onClose: (event: WebSocketEventMap["close"]) => {
        return;
      },
      onMessage: (event: WebSocketEventMap["message"]) => {
        toast.remove();
        if (event.data.includes("FINISHED")) {
          setShouldWebSocketConnect(false);
          toast.success("Chart has been regenerated");
        } else if (event.data.includes("ERROR")) {
          setShouldWebSocketConnect(false);
          toast.error("Error occurred while regenerating the chart.");
        }
      },
      onError: (event: WebSocketEventMap["error"]) => {
        setShouldWebSocketConnect(false);
        toast.error("Error occurred while generating the chart files.");
      },
      protocols: ["Authorization", token],
    },
    shouldWebSocketConnect
  );

  const getBaseUrl = () => {
    if (chartType === ChartTypeEnum.GENERAL_ELECTION) {
      return "/elections/general";
    } else if (chartType === ChartTypeEnum.NON_PARTISAN) {
      return "/elections/non-partisan";
    } else if (chartType === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "/elections/partisan";
    } else if (chartType === ChartTypeEnum.PRESIDENTIAL_PRIMARY) {
      return "/elections/presidential";
    }
  };

  const getCharts = useCallback(
    async (
      query: string,
      status: ChartStatus,
      orderBy: ChartOrderBy,
      showCompletedDeck: boolean
    ) => {
      if (debouncedQuery) {
        setLoading(true);
      }

      try {
        setLoading(true);
        const newCharts = await ChartsService.getChartsByType(
          status,
          chartType,
          query,
          orderBy,
          status === "Completed" ? showCompletedDeck : undefined
        );
        const electionCounties = newCharts.map((chart) => {
          return chart.county;
        });
        const uniqueCounties = electionCounties.filter(function (
          elem,
          pos,
          arr
        ) {
          return arr.indexOf(elem) === pos;
        });
        setCounties(uniqueCounties);

        // for each of the unique counties, look for election dates
        let uniqueCountiesWithElectionDates: string[] = [];
        uniqueCounties.forEach((county) => {
          let electionDates: string[] = [];
          newCharts.forEach((chart) => {
            if (chart.county === county) {
              electionDates.push(chart.electionDate);
            }
          });
          const uniqueElectionDates = electionDates.filter(function (
            elem,
            pos,
            arr
          ) {
            return arr.indexOf(elem) === pos;
          });
          uniqueElectionDates.forEach((electionDate) => {
            uniqueCountiesWithElectionDates.push(`${county} - ${electionDate}`);
          });
        });
        const chartsGroupedByCounty = uniqueCountiesWithElectionDates.map(
          (countyWithDate) => {
            const parts = countyWithDate.split(" - ");
            const county = parts[0];
            const electionDate = parts[1];
            return newCharts
              .filter(
                (chart: any) =>
                  chart.county === county && chart.electionDate === electionDate
              )
              .sort((a, b) => {
                // NOTE: this sorts the charts for each election date newest to oldest based on updatedAt
                if (a.modifiedAt < b.modifiedAt) {
                  return 1;
                }
                if (a.modifiedAt > b.modifiedAt) {
                  return -1;
                }
                return 0;
              })
              .map((c: any) => {
                return c;
              });
          }
        );
        setCharts(chartsGroupedByCounty);
      } catch (e) {
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedQuery, chartType]
  );

  useEffect(() => {
    getCharts(query, status, orderBy, showCompletedTestDecks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, chartType]);

  useEffect(() => {
    if (
      !Object.keys(ChartStatusEnum).includes(searchParams.get("status") || "")
    )
      return setSearchParams({});

    if (
      status !== "Completed" &&
      searchParams.get("showCompletedDeck") !== null
    )
      return setSearchParams({ status });

    if (
      status === "Completed" &&
      searchParams.get("showCompletedDeck") === null
    )
      return setSearchParams({
        status,
        showCompletedDeck: String(showCompletedTestDecks),
      });
  }, [searchParams, setSearchParams, showCompletedTestDecks, status]);

  const editTestDeck = (chartId: string) => {
    navigate(`/mark-ballot/${chartId}`);
  };

  const startNewChart = () => {
    navigate(`${getBaseUrl()}/create`);
  };

  const duplicateChart = (chartId: string) => {
    navigate(`${getBaseUrl()}/duplicate/${chartId}`);
  };

  const updateStatus = (newStatus: ChartStatus) => {
    setStatus(newStatus);
    let newOrderBy = orderBy;
    if (newStatus === "Completed" && orderBy === "false") {
      newOrderBy = "true";
    } else if (newStatus !== "Completed" && orderBy === "true") {
      newOrderBy = "false";
    }
    setOrderBy(newOrderBy);
    setSearchParams({ status: newStatus });
    getCharts(query, newStatus, newOrderBy, showCompletedTestDecks);
  };

  const updateOrderBy = (newOrderBy: ChartOrderBy) => {
    setOrderBy(newOrderBy);
    getCharts(query, status, newOrderBy, showCompletedTestDecks);
  };

  const updateShowCompletedTestDecks = (show: boolean) => {
    setShowCompletedTestDecks(show);
    setSearchParams({ status, showCompletedDeck: String(show) });
    getCharts(query, status, orderBy, show);
  };

  const deleteChart = async (id: string) => {
    await ChartsService.deleteChart(id);
    await getCharts(query, status, orderBy, showCompletedTestDecks);
  };

  const downloadAll = async (chartIds: string[], type: "charts" | "decks") => {
    await toast.promise(ChartsService.downloadAll(chartIds, type === "decks"), {
      loading: "Loading...",
      error: "Files download failed!",
      success: ({ data, filename }) => {
        const href = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
        return "Successful download!";
      },
    });
  };

  const downloadChart = async (chartId: string, type: "download" | "print") => {
    await toast.promise(ChartsService.getS3Link(chartId, type), {
      loading: "Loading...",
      success: (downloadLink) => {
        const a = document.createElement("a");
        a.href = downloadLink;
        document.body.appendChild(a);
        a.click();
        a.parentNode!.removeChild(a);
        return "Successful download!";
      },
      error: "Download failed!",
    });
  };

  const downloadTestDeck = async (chartId: string) => {
    await toast.promise(ChartsService.getFullDeckS3Link(chartId), {
      loading: "Loading...",
      success: (downloadLink) => {
        const a = document.createElement("a");
        a.href = downloadLink;
        document.body.appendChild(a);
        a.click();
        a.parentNode!.removeChild(a);
        return "Successful download!";
      },
      error: "Download failed!",
    });
  };

  const sendRequest = () => {
    setWasRequestSent(true);
  };

  const getDate = (date: string): string => {
    const d = new Date(date);
    return `${d.toLocaleString("en-US", {
      month: "long",
    })} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  };

  const regenerateChart = async (id: string) => {
    const url = `${process.env.REACT_APP_SERVICE_URL?.replace(
      "http://",
      "ws://"
    ).replace("https://", "wss://")}/charts/admin/process/${id}`;

    const session = await CognitoService.getSession();
    if (session && !token) {
      setToken(session.getIdToken().getJwtToken());
    }
    setWebsocketUrl(url);
    setShouldWebSocketConnect(true);
  };

  const regenerateTestDeck = async (id: string) => {
    const url = `${process.env.REACT_APP_SERVICE_URL?.replace(
      "http://",
      "ws://"
    ).replace("https://", "wss://")}/charts/generate-full-test-deck/${id}`;

    const session = await CognitoService.getSession();
    if (session && !token) {
      setToken(session.getIdToken().getJwtToken());
    }
    setWebsocketUrl(url);
    setShouldWebSocketConnect(true);
  };

  return {
    loading,
    query,
    debouncedQuery,
    status,
    orderBy,
    charts,
    wasRequestSent,
    showCompletedTestDecks,
    counties,
    chartType,

    downloadChart,
    downloadTestDeck,
    downloadAll,
    sendRequest,
    editTestDeck,
    startNewChart,
    duplicateChart,
    deleteChart,
    updateStatus,
    updateOrderBy,
    updateShowCompletedTestDecks,
    setQuery,
    getDate,
    regenerateChart,
    regenerateTestDeck,
  };
}
